import { unescape } from "lodash";
import { marked } from "marked";
import { uniq } from "@utils/toolBox";

export const useDecodeMessage = () => {
  const decodeBold = (text) => {
    if (!text) return "";
    return text.replace(/(\*{2})([^*]*)(\*{2})/g, "$2");
  };

  const decodeItalic = (text) => {
    if (!text) return "";
    return text.replace(/(_)([^_]*)(_)/g, "$2");
  };

  const decodeMessage = (text) => {
    // takes a string like "this is *my* message" and returns "this is <strong>my</strong> message"
    // usefully for previewing whatsapp messages
    if (!text) return "";
    return decodeItalic(decodeBold(unescape(text)));
  };

  // Like decodeMessage but preserves underscores (used for identifiers / card names
  // where underscores are not italic markers).
  const decodeName = (text) => {
    if (!text) return "";
    return unescape(text);
  };

  const decodeAiInsightsMessage = (message, format) => {
    if (message == null) return;
    let decodedMessage = message;

    // Add breaklines after headers if missing
    const missingBreaklines = /\.#{1,6}/g;
    decodedMessage = decodedMessage?.replaceAll(missingBreaklines, (match) => `${match[0]}\n\n${match.slice(1)}`);

    // Explicit options: the shared marked instance's globals depend on which modules have loaded.
    if (format === "html") decodedMessage = marked.parse(decodedMessage, { breaks: true, gfm: true });

    // Replace @refs: ['B5'] with (B5)
    const regex1 = /(@refs\s?:\s?\[\s?.+?\s?\])/g;
    decodedMessage = decodedMessage?.replace(regex1, (match, p1) => {
      const regex = /(&quot;([A-Za-z0-9_]+)&quot;)|(&#39;([A-Za-z0-9_]+)&#39;)|('([A-Za-z0-9_]+)')/g;
      const names = [...p1.matchAll(regex)].map(
        ([, , doubleQuoted, , singleQuoted, , plainQuoted]) => doubleQuoted || singleQuoted || plainQuoted,
      );
      const jointNames = uniq(names).join(", ");
      if (!jointNames) return "";
      if (format === "string") return `(${jointNames})`;
      return `<span class="assistant-message__question-item__text--hint">(${jointNames})</span>`;
    });

    // Quotes are emitted as `*"…"* — @resp(token, message_id)` — the resp marker may land
    // inside the italics or right after it. In html mode the italic is already an <em>, so we
    // fold the quote text + resp marker into one self-contained, clickable .assistant-quote
    // block (independent of the surrounding list/paragraph markup). The optional message_id
    // drives click-to-source. In string mode (clipboard) we just inline the respondent id,
    // truncated to its last 4 chars like the html path — the full token is a functional id
    // for the model, never client-facing.
    if (format === "string") {
      decodedMessage = decodedMessage?.replace(
        /\s*[—–-]?\s*@resp\(\s*([^,)]+?)\s*(?:,\s*\d+\s*)?\)/g,
        (match, respondentToken) => ` — Respondent #${respondentToken.trim().slice(-4)}`,
      );
    } else {
      const buildQuote = (quoteText, respondentToken, messageId) => {
        const token = respondentToken.trim().slice(-4);
        const messageIdAttr = messageId ? ` data-message-id="${messageId}"` : "";
        return `<span class="assistant-quote" data-respondent-token="${token}"${messageIdAttr}><span class="assistant-quote__text">${quoteText.trim()}</span><span class="assistant-quote__badge">#${token}</span></span>`;
      };
      const respInsideEm = /<em>((?:(?!<\/?em>)[^\n])+?)\s*[—–-]\s*@resp\(\s*([^,)]+?)\s*(?:,\s*(\d+)\s*)?\)\s*<\/em>/g;
      const respAfterEm = /<em>((?:(?!<\/?em>)[^\n])+?)<\/em>\s*[—–-]\s*@resp\(\s*([^,)]+?)\s*(?:,\s*(\d+)\s*)?\)/g;
      // Any leftover resp marker not attached to a quote stays a plain hint.
      const respBare = /[—–-]?\s*@resp\(\s*([^,)]+?)\s*(?:,\s*\d+\s*)?\)/g;
      decodedMessage = decodedMessage
        ?.replace(respInsideEm, (match, quoteText, respondentToken, messageId) => buildQuote(quoteText, respondentToken, messageId))
        ?.replace(respAfterEm, (match, quoteText, respondentToken, messageId) => buildQuote(quoteText, respondentToken, messageId))
        ?.replace(respBare, (match, respondentToken) => `<span class="assistant-message__question-item__text--hint">Respondent #${respondentToken.trim().slice(-4)}</span>`);
    }

    const regex2 = /\{(?:(?!<br>)[^}\n])*\}/g;
    decodedMessage = decodedMessage?.replaceAll(regex2, "");
    return decodedMessage;
  };

  return {
    decodeMessage,
    decodeName,
    decodeAiInsightsMessage,
  };
};
